import React, { useEffect, useRef, useState } from 'react';
import BusinessSearchForm from 'src/components/real-time-screening/BusinessSearchForm';
import MatchLiklihoodCards from 'src/components/MatchLiklihoodCards';
import ResultsGrid from '../components/ResultsGrid';
import { IScreeningResultsFilters, IBusinessScreeningSearchParams } from 'src/types';
import IScreenedResult from '@cc/shared-service/src/server/types/response/partials/IScreenedResult';
import * as screeningService from 'src/services/screening.service';
import * as notificationService from 'src/services/notification.service';
import { LikliHood } from 'src/enums';
import * as notify from 'src/services/notification.service';
import EnterDataMessage from 'src/components/real-time-screening/EnterDataMessage';
import NoResultsMessage from 'src/components/real-time-screening/NoResultsMessage';

const BusinessLightScreeningPage: React.FC = () => {
  const [pristine, setPristine] = useState<boolean>(true);
  const [screenedResults, setScreenedResults] = useState<IScreenedResult[]>([]);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [nextPage, setNextPage] = useState<string>('');
  const [pageStates, setPageStates] = useState<string[]>(['']);
  const [currentRecordsPerPage, setCurrentRecordsPerPage] = useState<number>(10);
  const [totalRecordsAvailable, setTotalRecordsAvailable] = useState<number>(0);
  const [isScreening, setIsScreening] = useState<boolean>(false);
  const [filters, setFilters] = useState<IScreeningResultsFilters>({
    selectedLikliHood: LikliHood.All,
    subCategory: '',
    category: '',
    database: '',
    updatedDate: '',
  });
  const [filtersData, setFiltersData] = useState({
    categories: [],
    subcategories: [],
    databases: [],
  });
  const [sortBy, setSortBy] = useState<string>('match|desc');
  const [searchParameters, setSearchParameters] = useState<IBusinessScreeningSearchParams>();
  const [selectedLikliHood, setSelectedLikliHood] = useState('');
  const [likliHoodsCount, setLikliHoodsCount] = useState({
    exact: 0,
    veryHigh: 0,
    high: 0,
    medium: 0,
    low: 0,
    veryLow: 0,
  });
  const [screeningComplete, setScreeningComplete] = useState(false);
  const [jobEntityId, setJobEntityId] = useState('');
  let searchParamsChanged = useRef(false);

  const startScreening = () => {
    setIsScreening(true);
    screeningService
      .screenBusiness(
        searchParameters,
        { ...filters, selectedLikliHood },
        nextPage,
        currentRecordsPerPage,
        sortBy,
        jobEntityId,
      )
      // @ts-ignore
      .then((response) => {
        const data = response.data.data;
        setScreenedResults(data.results);
        setTotalPages(data.paging.totalPages);
        setPageStates([...pageStates, data.paging.nextPage]);
        setNextPage(data.paging.nextPage || '');
        setTotalRecordsAvailable(data.paging.totalRecordsAvailable);
        setLikliHoodsCount(data.counts);
        setFiltersData(data.filtersData);
        setScreeningComplete(true);
        setJobEntityId(data.jobEntityId);
      })
      .catch((err) => {
        if (!err.response.handled) {
          if (err.response && err.response.data && err.response.data.error.message)
            notify.error(err.response.data.error.message);
        }
        setScreenedResults([]);
        setTotalPages(0);
        setNextPage('');
        setTotalRecordsAvailable(0);
        setLikliHoodsCount({ exact: 0, veryHigh: 0, high: 0, medium: 0, low: 0, veryLow: 0 });
        setScreeningComplete(false);
      })
      .finally(() => {
        setIsScreening(false);
        if (searchParamsChanged.current) {
          searchParamsChanged.current = false;
        }
      });
  };

  const clearAllFilters = () => {
    setNextPage('');
    setFilters({
      database: '',
      category: '',
      subCategory: '',
      selectedLikliHood: LikliHood.All,
      updatedDate: '',
    });
    setSortBy('match|desc');
    setSelectedLikliHood(LikliHood.All);
  };
  const searchParametersChanged = async (params: IBusinessScreeningSearchParams) => {
    setScreeningComplete(false);
    setPristine(false);
    searchParamsChanged.current = true;
    setSearchParameters(params);
    setNextPage('');
    setJobEntityId('');
    clearAllFilters();
  };
  const likliHoodChanged = async (likliHood: string) => {
    removePages();
    setSelectedLikliHood(likliHood);
  };
  const currentPageChanged = async (pageNumber: number) => {
    const pageStatesCopy = [...pageStates];
    if (pageNumber > currentPage) {
      setNextPage(pageStatesCopy[pageStatesCopy.length - 1] || '');
    } else {
      // pop top two pages
      pageStatesCopy.pop();
      pageStatesCopy.pop();
      setNextPage(pageStatesCopy[pageStatesCopy.length - 1] || '');
    }
    setPageStates([...pageStatesCopy]);
    setCurrentPage(pageNumber);
  };
  const setRecordsPerPage = async (recordsCount: number) => {
    removePages();
    setCurrentRecordsPerPage(recordsCount);
  };
  const filtersChanged = async (filters: IScreeningResultsFilters) => {
    removePages();
    setFilters(filters);
  };
  const sortingChanged = async (sort: string) => {
    removePages();
    setSortBy(sort);
  };

  useEffect(() => {
    if (!pristine) {
      console.log('Calling startScreening');
      startScreening();
    }
  }, [
    searchParameters,
    filters,
    currentPage,
    currentRecordsPerPage,
    sortBy,
    pristine,
    selectedLikliHood,
  ]);

  const removePages = () => {
    setPageStates(['']);
    setNextPage('');
    setCurrentPage(1);
  };

  const downloadCsv = async () => {
    if (screenedResults.length) {
      window.open(
        `${process.env.REACT_APP_BASE_URL}export/csv/?entityId=${screenedResults[0].job_entity_id}`,
        '_blank',
        'noopener,noreferrer',
      );
    } else {
      notificationService.warning('No records found');
    }
  };

  return (
    <>
      <BusinessSearchForm 
        screen={searchParametersChanged}
        screeningComplete={screeningComplete}
      />

      <div className="row">
        <div className="col-lg-12 mt-10">
          <div className="card-title d-flex justify-content-between align-items-center">
            <h4>RESULTS</h4>
            {/* <button
              className="button btn-with-icon md"
              onClick={downloadCsv}
            >
              <img src={require("src/assets/images/icons/download-icon.svg").default} alt="" /> Export
            </button> */}
          </div>
        </div>
        <div className="col-lg-12">
          <MatchLiklihoodCards
            likliHoodChanged={likliHoodChanged}
            likliHoodsCount={likliHoodsCount}
          />
        </div>

        {
          pristine
          &&
          <EnterDataMessage
            mt='mt-2'
            message='Enter customer name to see screening results'
          />
        }

        <div className={`overlay-loader ${isScreening ? 'active' : ''}`}>
          <div className={`loader text-center`}>
            <img src={require('src/assets/images/icons/company_detail_spinner_transparent.gif')} alt="" />
            <p className='color-monoNormal'>Screening...</p>
          </div>
        </div>

        <div data-testid={isScreening ? 'loading' : 'loaded'}></div>
        {
          !pristine
          &&
          !!screenedResults.length
          &&
          <ResultsGrid
            results={screenedResults}
            fetchPage={currentPageChanged}
            currentPage={currentPage}
            currentRecordsPerPage={currentRecordsPerPage}
            setRecordsPerPage={setRecordsPerPage}
            totalRecordsAvailable={totalRecordsAvailable}
            totalPages={totalPages}
            filtersChanged={filtersChanged}
            filtersData={filtersData}
            isScreening={false}
            sortingChanged={sortingChanged}
            sortBy={sortBy}
          />
        }
        {
          !pristine
          &&
          !screenedResults.length
          &&
          !isScreening
          &&
          <NoResultsMessage mt='mt-2' />
        }
      </div>
    </>
  );
};

export default BusinessLightScreeningPage;
